import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { requisicaoAutenticada } from '../servicos/http';
import { lerComoBase64 } from '../servicos/arquivo';

export interface ResultadoDoLote {
  readonly criados: number;
  readonly ignorados: number;
}

export interface ItemParaInserir {
  readonly calculoId: string;
  readonly emailId: string;
  readonly dataAgendamento: string;
}

/**
 * As três ações sobre um item do cronograma.
 *
 * Todas invalidam a fila do cliente, porque todas mudam o status da linha — e
 * uma tela que continua mostrando "Agendado" depois do envio faz o operador
 * clicar de novo.
 */

interface Resultado {
  readonly id: string;
  readonly status: string;
  readonly destinatarios: readonly string[];
  /** `true` quando o e-mail foi montado e não saiu (`EMAIL_ENVIO_REAL=0`). */
  readonly ensaio?: boolean;
}

function useAcao<E>(
  clienteId: string,
  executar: (entrada: E) => Promise<Resultado>,
): UseMutationResult<Resultado, Error, E> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: executar,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['cronograma', clienteId] });
    },
  });
}

export function useEnviarAgora(clienteId: string): UseMutationResult<Resultado, Error, string> {
  return useAcao(clienteId, (emailId: string) =>
    requisicaoAutenticada<Resultado>(`/icms/emails/${emailId}/send`, {
      metodo: 'POST',
      corpo: {},
    }),
  );
}

export function useReprogramar(
  clienteId: string,
): UseMutationResult<Resultado, Error, { emailId: string; dataAgendamento: string }> {
  return useAcao(clienteId, ({ emailId, dataAgendamento }) =>
    requisicaoAutenticada<Resultado>(`/icms/emails/${emailId}/schedule`, {
      metodo: 'POST',
      corpo: { dataAgendamento },
    }),
  );
}

export function useCancelar(
  clienteId: string,
): UseMutationResult<Resultado, Error, { emailId: string; motivo?: string }> {
  return useAcao(clienteId, ({ emailId, motivo }) =>
    requisicaoAutenticada<Resultado>(`/icms/emails/${emailId}/cancelar`, {
      metodo: 'POST',
      corpo: motivo === undefined ? {} : { motivo },
    }),
  );
}

/**
 * Inserção e remoção em lote (RN-082).
 *
 * Não usam `useAcao` porque a resposta é uma contagem, não uma linha: o lote não
 * devolve status nem destinatários, devolve quantos entraram e quantos foram
 * ignorados. Forçá-los no mesmo tipo daria um `Resultado` com campos vazios.
 */
function useLote<E>(
  clienteId: string,
  executar: (entrada: E) => Promise<ResultadoDoLote>,
): UseMutationResult<ResultadoDoLote, Error, E> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: executar,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['cronograma', clienteId] });
      // A lista de disponíveis encolhe a cada inserção — sem invalidá-la, o modal
      // reaberto ainda ofereceria o que acabou de entrar na fila.
      void cliente.invalidateQueries({ queryKey: ['speds-disponiveis', clienteId] });
    },
  });
}

export function useInserirLote(
  clienteId: string,
): UseMutationResult<ResultadoDoLote, Error, { ano: number; itens: readonly ItemParaInserir[] }> {
  return useLote(clienteId, ({ ano, itens }) =>
    requisicaoAutenticada<ResultadoDoLote>('/icms/cronograma/inserir-lote', {
      metodo: 'POST',
      corpo: { clienteId, ano, itens },
    }),
  );
}

export function useRemoverLote(
  clienteId: string,
): UseMutationResult<ResultadoDoLote, Error, readonly string[]> {
  return useLote(clienteId, (ids) =>
    requisicaoAutenticada<ResultadoDoLote>('/icms/cronograma/remover-lote', {
      metodo: 'POST',
      corpo: { clienteId, ids },
    }),
  );
}

/* ─── Anexos ──────────────────────────────────────────────────────────────── */

export interface EtapaLiberada {
  readonly emailId: string;
  readonly rotulo: string;
  readonly dataAgendamento: string;
}

export interface ResultadoDoAnexo {
  readonly id: string;
  readonly anexos: readonly { readonly nome: string; readonly rotulo: string }[];
  /** RN-622 — as etapas que este anexo destravou, quando destravou alguma. */
  readonly etapasLiberadas?: readonly EtapaLiberada[];
  /** D-008 — o certificado de apropriação fez o mês contar como aproveitado. */
  readonly mesAproveitado?: boolean;
}

/**
 * Anexar um documento a um item — de dentro do detalhe ou direto da linha.
 *
 * As duas telas mandam para o mesmo endpoint e precisam invalidar as mesmas
 * consultas: a fila (o clipe e os documentos da linha mudam) e o detalhe
 * aberto. Enquanto cada uma tinha a sua mutação, anexar pela linha deixava o
 * modal do mesmo item exibindo a lista antiga.
 */
export function useAnexarNoItem(
  clienteId: string,
): UseMutationResult<ResultadoDoAnexo, Error, { emailId: string; chave: string; arquivo: File }> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: async ({ emailId, chave, arquivo }) =>
      requisicaoAutenticada<ResultadoDoAnexo>(`/icms/emails/${emailId}/attachments`, {
        metodo: 'POST',
        corpo: {
          chave,
          nome: arquivo.name,
          conteudoBase64: await lerComoBase64(arquivo),
          ...(arquivo.type === '' ? {} : { tipoConteudo: arquivo.type }),
        },
      }),
    onSuccess: (_dados, { emailId }) => {
      void cliente.invalidateQueries({ queryKey: ['cronograma', clienteId] });
      void cliente.invalidateQueries({ queryKey: ['aprovacao', emailId] });
      // D-008 — o certificado de apropriação muda `mesesAproveitamento`, que
      // aparece na apuração e nos painéis.
      void cliente.invalidateQueries({ queryKey: ['speds', clienteId] });
      void cliente.invalidateQueries({ queryKey: ['dashboard-cliente', clienteId] });
      void cliente.invalidateQueries({ queryKey: ['visao-geral'] });
    },
  });
}

/** Remove um anexo (com `localizacao`) ou todos os do item (sem ela). */
export function useRemoverAnexo(
  clienteId: string,
): UseMutationResult<{ removidos: number }, Error, { emailId: string; localizacao?: string }> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ emailId, localizacao }) =>
      requisicaoAutenticada<{ removidos: number }>(
        `/icms/emails/${emailId}/attachments${
          localizacao === undefined ? '' : `?localizacao=${encodeURIComponent(localizacao)}`
        }`,
        { metodo: 'DELETE' },
      ),
    onSuccess: (_dados, { emailId }) => {
      void cliente.invalidateQueries({ queryKey: ['cronograma', clienteId] });
      void cliente.invalidateQueries({ queryKey: ['aprovacao', emailId] });
    },
  });
}
